import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import {
  AnimatedComponent,
  AnimationType,
} from '@monorepo/shared/components/AnimatedComponent';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { useMoveFocus } from '@monorepo/shared/hooks/useMoveFocus';
import { FieldResponse } from 'mapistry-shared';
import React from 'react';
import { FieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import { composeEmptyVersionFormValue, VersionFormValue } from './helpers';
import { HistoricalValueCard } from './HistoricalValueCard';

const AddButton = styled(Button)`
  width: 100%;
  margin-bottom: 1rem;
  border: 1px dashed ${({ theme }) => theme.colors.builderBlue};
  border-radius: 3px;
`;

type EditHistoricalValueListProps = {
  field: FieldResponse;
  organizationId: string;
  projectId: string | undefined;
};

export function EditHistoricalValueList({
  field,
  organizationId,
  projectId,
}: EditHistoricalValueListProps) {
  const arrayName = field.id;

  const { moveFocus, focusCatcherRef } = useMoveFocus();

  return (
    <FieldArray<VersionFormValue> name={arrayName}>
      {({ fields: formFields }) => {
        const versions = formFields.value ?? [];
        const versionCount = versions.length;
        const latestVersion = versions[versionCount - 1];
        const isAddButtonDisabled = versionCount > 1 && !latestVersion?.begin;

        return (
          <>
            <AddButton
              color="primary"
              disabled={isAddButtonDisabled}
              onClick={() => formFields.push(composeEmptyVersionFormValue())}
              ref={focusCatcherRef}
            >
              <AddCircleOutline />
              Create new value
            </AddButton>
            <AnimatedComponent
              initial={false}
              type={AnimationType.slideDownPresence}
            >
              {[...versions.keys()].reverse().map((formFieldIndex) => {
                const formFieldName = `${arrayName}[${formFieldIndex}]`;
                const isFirstInHistory = formFieldIndex === 0;
                const isLastInHistory = formFieldIndex === versionCount - 1;
                const nextVersion = versions[formFieldIndex + 1];
                const previousVersion = versions[formFieldIndex - 1];
                const endDate = isLastInHistory
                  ? null
                  : nextVersion?.begin ?? null;
                return (
                  <HistoricalValueCard
                    key={formFieldName}
                    endDate={endDate}
                    field={field}
                    formFieldName={formFieldName}
                    isFirstInHistory={isFirstInHistory}
                    isLastInHistory={isLastInHistory}
                    isReadOnly={!isLastInHistory}
                    previousStartDate={previousVersion?.begin ?? null}
                    onDelete={
                      isLastInHistory && !isFirstInHistory
                        ? () => {
                            formFields.remove(formFieldIndex);
                            moveFocus();
                          }
                        : undefined
                    }
                    organizationId={organizationId}
                    projectId={projectId}
                  />
                );
              })}
            </AnimatedComponent>
          </>
        );
      }}
    </FieldArray>
  );
}
